import { toast } from "sonner";
import { useUpdateAIFieldDefinition } from "@/hooks/api/use-deep-research";
import { AIQueryColumnModal } from "@/pages/deep-research/report/ai-query-column-modal";
import type {
  AIFieldDefinition,
  UpdateAIFieldRequest,
} from "@/types/deep-research-reports";

interface AIFieldEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  field: AIFieldDefinition | null;
  onSuccess?: () => void;
}

export function AIFieldEditModal({
  isOpen,
  onClose,
  field,
  onSuccess,
}: AIFieldEditModalProps) {
  const { mutate: updateField } = useUpdateAIFieldDefinition();

  const handleSave = async (updatedField: Partial<AIFieldDefinition>) => {
    if (!field) return;

    // Build the update payload with only fields that should be sent
    const updatePayload: UpdateAIFieldRequest = {};

    // Always include ai_instructions if present
    if (updatedField.ai_instructions !== undefined) {
      updatePayload.ai_instructions = updatedField.ai_instructions;
    }

    // Include content_type if it's being changed
    if (updatedField.content_type !== undefined) {
      updatePayload.content_type = updatedField.content_type;
    }

    // Include output_shape if it's being changed
    if (updatedField.output_shape !== undefined) {
      updatePayload.output_shape = updatedField.output_shape;
    }

    // Include allowed_values if provided and relevant
    if (updatedField.allowed_values !== undefined) {
      // If we're changing to or already are a pick_list, include allowed_values
      const targetContentType = updatedField.content_type || field.content_type;
      if (targetContentType === "pick_list") {
        updatePayload.allowed_values = updatedField.allowed_values;
      }
    }

    updateField(
      { field_id: field.id, body: updatePayload },
      {
        onSuccess: () => {
          toast.success("AI field updated successfully");
          onSuccess?.();
          onClose();
        },
        onError: (error) => {
          console.error("Failed to update AI field:", error);
          toast.error("Failed to update AI field");
        },
      },
    );
  };

  if (!field) return null;

  return (
    <AIQueryColumnModal
      open={isOpen}
      onClose={onClose}
      onSave={handleSave}
      initialData={field}
      isEditing={true}
    />
  );
}
